import React, { useState, useEffect, useRef } from 'react';
import { activityAPI, categoryAPI, objectiveAPI, classAPI, groupAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Coins, Plus, Pencil, Trash2, Target, Calendar as CalendarIcon, Zap, Users, CheckCircle2, Clock, ChevronLeft, ChevronRight, Play, Eye, X, Filter, Repeat, Award, UsersRound, Send, ChevronUp, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';
import { formatCoins, formatDate } from '../../lib/utils';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { RichTextEditor, RichTextDisplay, stripHtml } from '../common/RichTextEditor';

const CATEGORY_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

export default function ActivitiesTab({ classId, isProfessor, onRefresh }) {
  const [activities, setActivities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [completionsSummary, setCompletionsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    coin_value: 10,
    xp_value: 0,
    xp_breakdown: [],
    learning_objective_id: '',
    category_id: '',
    due_date: '',
    is_active: true,
    is_recurring: false,
    is_group_activity: false,
    use_multi_xp: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarActivities, setCalendarActivities] = useState([]);
  
  // Student activity status
  const [myActivitiesStatus, setMyActivitiesStatus] = useState(null);

  // Category management
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', color: '#3B82F6', icon: 'folder' });
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Activity status view (professor)
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedActivityStatuses, setSelectedActivityStatuses] = useState(null);
  
  // NEW: Selected activity detail panel
  const [selectedActivity, setSelectedActivity] = useState(null);
  const detailRef = useRef(null);
  const listRef = useRef(null);
  
  // NEW: Category filter
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'compact'
  
  // NEW: Batch award dialog
  const [batchAwardDialogOpen, setBatchAwardDialogOpen] = useState(false);
  const [batchAwardActivity, setBatchAwardActivity] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [batchAwardNote, setBatchAwardNote] = useState('');
  const [students, setStudents] = useState([]);
  const [completionCounts, setCompletionCounts] = useState(null);
  
  // NEW: Groups for award
  const [groups, setGroups] = useState([]);
  const [myGroupInfo, setMyGroupInfo] = useState(null); // {group_id, is_leader}
  const [awardMode, setAwardMode] = useState('individual'); // 'individual' or 'group'
  const [selectedGroup, setSelectedGroup] = useState('');
  const [awardedStudentIds, setAwardedStudentIds] = useState(new Set());
  const [awardedGroupIds, setAwardedGroupIds] = useState(new Set());
  const [subscribers, setSubscribers] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [classId]);

  useEffect(() => {
    loadCalendar();
  }, [currentMonth, classId]);

  const loadData = async () => {
    try {
      const [activitiesRes, categoriesRes, objectivesRes] = await Promise.all([
        activityAPI.list(classId),
        categoryAPI.list(classId),
        objectiveAPI.list(classId)
      ]);
      setActivities(activitiesRes.data);
      setCategories(categoriesRes.data);
      setObjectives(objectivesRes.data);

      if (!isProfessor) {
        const [subsRes, statusRes, myGroupsRes] = await Promise.all([
          activityAPI.getSubscriptions(classId),
          activityAPI.getMyActivitiesStatus(classId),
          groupAPI.getMyGroups(classId).catch(() => ({ data: [] }))
        ]);
        setSubscriptions(subsRes.data);
        setMyActivitiesStatus(statusRes.data);
        // Find student's group and leader status
        const mg = (myGroupsRes.data || []);
        if (mg.length > 0) {
          setMyGroupInfo({ group_id: mg[0].id, is_leader: mg[0].is_leader || false });
        }
      } else {
        const [summaryRes, studentsRes, groupsRes] = await Promise.all([
          activityAPI.getCompletionsSummary(classId),
          classAPI.getStudents(classId),
          groupAPI.list(classId, true)
        ]);
        setCompletionsSummary(summaryRes.data);
        setStudents(studentsRes.data || []);
        setGroups(groupsRes.data || []);
      }
    } catch (error) {
      toast.error('Error al cargar actividades');
    } finally {
      setLoading(false);
    }
  };
  
  const loadCompletionCounts = async (activityId) => {
    try {
      const res = await activityAPI.getCompletionCounts(classId, activityId);
      setCompletionCounts(res.data);
    } catch (error) {
      console.error('Error loading completion counts');
    }
  };

  const loadSubscribers = async (activityId) => {
    try {
      const res = await activityAPI.getSubscribers(classId, activityId);
      setSubscribers({ activity_id: activityId, list: res.data });
    } catch {
      toast.error('Error al cargar suscritos');
    }
  };

  const handleSelectActivity = (activity) => {
    setSelectedActivity(activity);
    // On mobile, open drawer
    if (window.innerWidth < 1024) {
      setMobileDetailOpen(true);
    }
  };

  
  const handleOpenBatchAward = async (activity) => {
    setBatchAwardActivity(activity);
    setSelectedStudents([]);
    setBatchAwardNote('');
    // Group activities force group mode
    setAwardMode(activity.is_group_activity ? 'group' : 'individual');
    setSelectedGroup('');
    setAwardedStudentIds(new Set());
    setAwardedGroupIds(new Set());
    setBatchAwardDialogOpen(true);
    
    // Load already-awarded students/groups for non-recurring activities
    if (!activity.is_recurring) {
      try {
        const res = await activityAPI.getCompletionCounts(classId, activity.id);
        const awarded = new Set((res.data.students || []).map(s => s.student_user_id));
        setAwardedStudentIds(awarded);
        // Load awarded groups
        if (res.data.awarded_group_ids) {
          setAwardedGroupIds(new Set(res.data.awarded_group_ids));
        }
      } catch {}
    }
  };
  
  const handleBatchAward = async () => {
    // Group award mode
    if (awardMode === 'group') {
      if (!selectedGroup) {
        toast.error('Selecciona un grupo');
        return;
      }
      
      setSubmitting(true);
      try {
        const res = await groupAPI.awardActivity(classId, batchAwardActivity.id, selectedGroup, batchAwardNote);
        toast.success(res.data.message);
        setBatchAwardDialogOpen(false);
        loadData();
        if (selectedActivity?.id === batchAwardActivity.id) {
          loadCompletionCounts(batchAwardActivity.id);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al otorgar coins');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    
    // Individual award mode
    if (selectedStudents.length === 0) {
      toast.error('Selecciona al menos un estudiante');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await activityAPI.awardBatch(classId, batchAwardActivity.id, selectedStudents, batchAwardNote);
      toast.success(`Coins otorgados a ${res.data.awarded_count} estudiantes`);
      if (res.data.skipped_count > 0) {
        toast.info(`${res.data.skipped_count} estudiantes ya tenían la actividad completada`);
      }
      setBatchAwardDialogOpen(false);
      loadData();
      // Reload completion counts if viewing this activity
      if (selectedActivity?.id === batchAwardActivity.id) {
        loadCompletionCounts(batchAwardActivity.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al otorgar coins');
    } finally {
      setSubmitting(false);
    }
  };
  
  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };
  
  const selectAllStudents = () => {
    const selectable = students.filter(s => !awardedStudentIds.has(s.user_id));
    if (selectedStudents.length === selectable.length && selectable.length > 0) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(selectable.map(s => s.user_id));
    }
  };

  const loadCalendar = async () => {
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const res = await activityAPI.getCalendar(classId, month, year);
      setCalendarActivities(res.data);
    } catch (error) {
      console.error('Error loading calendar');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      coin_value: 10,
      xp_value: 0,
      xp_breakdown: [],
      learning_objective_id: '',
      category_id: '',
      due_date: '',
      is_active: true,
      is_recurring: false,
      is_group_activity: false,
      use_multi_xp: false
    });
    setEditingActivity(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (activity) => {
    setEditingActivity(activity);
    const hasXpBreakdown = activity.xp_breakdown && activity.xp_breakdown.length > 0;
    setFormData({
      title: activity.title,
      description: activity.description,
      coin_value: activity.coin_value,
      xp_value: activity.xp_value || 0,
      xp_breakdown: activity.xp_breakdown || [],
      learning_objective_id: activity.learning_objective_id || '',
      category_id: activity.category_id || '',
      due_date: activity.due_date || '',
      is_active: activity.is_active,
      is_recurring: activity.is_recurring || false,
      is_group_activity: activity.is_group_activity || false,
      use_multi_xp: hasXpBreakdown
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.coin_value <= 0) {
      toast.error('El valor en coins debe ser mayor a 0');
      return;
    }
    
    if (formData.use_multi_xp) {
      const totalXp = formData.xp_breakdown.reduce((sum, item) => sum + (item.xp || 0), 0);
      if (totalXp === 0) {
        toast.error('Debes asignar al menos 1 XP a alguna habilidad');
        return;
      }
    } else if (formData.xp_value < 0 || formData.xp_value > 100) {
      toast.error('El valor de XP debe estar entre 0 y 100');
      return;
    }
    
    setSubmitting(true);
    try {
      const data = {
        title: formData.title,
        description: formData.description,
        coin_value: formData.coin_value,
        xp_value: formData.use_multi_xp ? 0 : formData.xp_value,
        xp_breakdown: formData.use_multi_xp ? formData.xp_breakdown.filter(item => item.xp > 0) : null,
        learning_objective_id: formData.use_multi_xp ? null : (formData.learning_objective_id || null),
        category_id: formData.category_id || null,
        due_date: formData.due_date || null,
        is_active: formData.is_active,
        is_recurring: formData.is_recurring,
        is_group_activity: formData.is_group_activity
      };
      
      if (editingActivity) {
        await activityAPI.update(classId, editingActivity.id, data);
        toast.success('Actividad actualizada');
      } else {
        await activityAPI.create(classId, data);
        toast.success('Actividad creada');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar actividad');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('¿Eliminar esta actividad?')) return;
    try {
      await activityAPI.delete(classId, activityId);
      toast.success('Actividad eliminada');
      setSelectedActivity(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSubscribe = async (activityId) => {
    try {
      await activityAPI.subscribe(classId, activityId);
      toast.success('¡Te has suscrito a la actividad!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al suscribirse');
    }
  };

  const handleUnsubscribe = async (activityId) => {
    try {
      await activityAPI.unsubscribe(classId, activityId);
      toast.success('Suscripción cancelada');
      loadData();
    } catch (error) {
      toast.error('Error al cancelar');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    try {
      await categoryAPI.create(classId, newCategory);
      toast.success('Categoría creada');
      setCategoryDialogOpen(false);
      setNewCategory({ name: '', color: '#3B82F6', icon: 'folder' });
      loadData();
    } catch (error) {
      toast.error('Error al crear categoría');
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || !editingCategory.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    try {
      await categoryAPI.update(classId, editingCategory.id, {
        name: editingCategory.name,
        color: editingCategory.color
      });
      toast.success('Categoría actualizada');
      setEditingCategory(null);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar categoría');
    }
  };

  const handleStartActivity = async (activityId) => {
    try {
      await activityAPI.updateStatus(classId, activityId, 'in_progress');
      toast.success('Actividad iniciada!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar actividad');
    }
  };

  const handleMarkCompleted = async (activityId) => {
    try {
      await activityAPI.updateStatus(classId, activityId, 'pending_approval');
      toast.success('Actividad enviada para aprobación');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar actividad');
    }
  };

  const handleViewStatuses = async (activityId) => {
    try {
      const res = await activityAPI.getStatuses(classId, activityId);
      setSelectedActivityStatuses(res.data);
      setStatusDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar estados');
    }
  };

  const handleChangeStudentStatus = async (activityId, studentId, newStatus) => {
    try {
      await activityAPI.updateStudentStatus(classId, activityId, studentId, newStatus);
      toast.success('Estado actualizado');
      const res = await activityAPI.getStatuses(classId, activityId);
      setSelectedActivityStatuses(res.data);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    }
  };

  const getMyActivityStatus = (activityId) => {
    if (!myActivitiesStatus?.activities) return 'not_started';
    const found = myActivitiesStatus.activities.find(a => a.activity_id === activityId);
    return found?.status || 'not_started';
  };

  const isSubscribed = (activityId) => {
    return subscriptions.some(s => s.subscription.activity_id === activityId);
  };

  const getSubscriptionStatus = (activityId) => {
    const sub = subscriptions.find(s => s.subscription.activity_id === activityId);
    return sub?.subscription.status;
  };

  // Calendar helpers
  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const getFirstDayOffset = () => {
    const start = startOfMonth(currentMonth);
    return getDay(start);
  };

  const getActivitiesForDay = (day) => {
    return calendarActivities.filter(a => {
      if (!a.due_date) return false;
      try {
        let dueDate;
        if (a.due_date.includes('T')) {
          dueDate = new Date(a.due_date);
        } else {
          const [year, month, dayNum] = a.due_date.split('-').map(Number);
          dueDate = new Date(year, month - 1, dayNum);
        }
        return isSameDay(dueDate, day);
      } catch {
        return false;
      }
    });
  };

  // Calculate total XP for an activity
  const getTotalXP = (activity) => {
    if (activity.xp_breakdown && activity.xp_breakdown.length > 0) {
      return activity.xp_breakdown.reduce((sum, x) => sum + x.xp, 0);
    }
    return activity.xp_value || 0;
  };

  // Filter activities by category and status
  const filteredActivities = activities.filter(a => {
    // Category filter
    if (filterCategory !== 'all') {
      if (filterCategory === 'none' && a.category_id) return false;
      if (filterCategory !== 'none' && a.category_id !== filterCategory) return false;
    }
    // Status filter (student only)
    if (filterStatus !== 'all' && !isProfessor && myActivitiesStatus?.activities) {
      const actStatus = myActivitiesStatus.activities.find(s => s.activity_id === a.id);
      const status = actStatus?.status || 'not_started';
      if (filterStatus === 'completed' && status !== 'completed') return false;
      if (filterStatus === 'in_progress' && status !== 'in_progress') return false;
      if (filterStatus === 'not_started' && status !== 'not_started') return false;
    }
    return true;
  }).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  // Summary counts by category
  const getSummaryByCategory = () => {
    const summary = { all: activities.length, none: 0 };
    categories.forEach(cat => { summary[cat.id] = 0; });
    
    activities.forEach(a => {
      if (!a.category_id) {
        summary.none++;
      } else if (summary[a.category_id] !== undefined) {
        summary[a.category_id]++;
      }
    });
    return summary;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const summaryCounts = getSummaryByCategory();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-heading font-bold text-slate-900">Actividades</h3>
          <p className="text-slate-500">Gestiona las actividades y recompensas de la clase</p>
        </div>
        {isProfessor && (
          <div className="flex gap-2">
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="manage-categories-btn">
                  Categorías
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Gestionar Categorías</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-3">
                    {categories.map(cat => (
                      <div key={cat.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        {editingCategory?.id === cat.id ? (
                          <div className="flex-1 flex items-center gap-2">
                            <Input
                              value={editingCategory.name}
                              onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                              className="h-8"
                            />
                            <div className="flex gap-1">
                              {CATEGORY_COLORS.slice(0, 4).map(color => (
                                <button
                                  key={color}
                                  type="button"
                                  className={`w-6 h-6 rounded-full border-2 ${editingCategory.color === color ? 'border-slate-900' : 'border-transparent'}`}
                                  style={{ backgroundColor: color }}
                                  onClick={() => setEditingCategory({...editingCategory, color})}
                                />
                              ))}
                            </div>
                            <Button size="sm" onClick={handleUpdateCategory}>Guardar</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingCategory(null)}>Cancelar</Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cat.color }} />
                              <span>{cat.name}</span>
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => setEditingCategory({ id: cat.id, name: cat.name, color: cat.color })}
                              >
                                <Pencil className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={async () => {
                                  await categoryAPI.delete(classId, cat.id);
                                  loadData();
                                }}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4">
                    <Label>Nueva Categoría</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="Nombre"
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                      />
                      <div className="flex gap-1">
                        {CATEGORY_COLORS.map(color => (
                          <button
                            key={color}
                            type="button"
                            className={`w-8 h-8 rounded-full border-2 ${newCategory.color === color ? 'border-slate-900' : 'border-transparent'}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setNewCategory({...newCategory, color})}
                          />
                        ))}
                      </div>
                    </div>
                    <Button onClick={handleCreateCategory} className="mt-2 w-full">
                      Agregar
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Button onClick={handleOpenCreate} data-testid="create-activity-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Actividad
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 lg:h-[calc(100vh-14rem)]">
        {/* Left Column: Activity List - independent scroll */}
        <div ref={listRef} className="lg:col-span-2 space-y-3 lg:overflow-y-auto lg:pr-2 lg:pb-4 scrollbar-thin">
          {/* Category Filter + View Toggle */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Filter className="w-4 h-4" />
              </div>
              <button
                onClick={() => setFilterCategory('all')}
                className={`px-2.5 py-1 rounded-full text-xs sm:text-sm transition-colors ${
                  filterCategory === 'all' 
                    ? 'bg-slate-900 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Todas ({summaryCounts.all})
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setFilterCategory(cat.id)}
                  className={`px-2.5 py-1 rounded-full text-xs sm:text-sm transition-colors flex items-center gap-1 ${
                    filterCategory === cat.id 
                      ? 'text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  style={filterCategory === cat.id ? { backgroundColor: cat.color } : {}}
                >
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                  {cat.name} ({summaryCounts[cat.id] || 0})
                </button>
              ))}
              {summaryCounts.none > 0 && (
                <button
                  onClick={() => setFilterCategory('none')}
                  className={`px-2.5 py-1 rounded-full text-xs sm:text-sm transition-colors ${
                    filterCategory === 'none' 
                      ? 'bg-slate-700 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Sin cat. ({summaryCounts.none})
                </button>
              )}
            </div>
            {/* View Toggle */}
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5 flex-shrink-0" data-testid="view-mode-toggle">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'cards' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}
                title="Vista tarjetas"
                data-testid="view-mode-cards"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('compact')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'compact' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}
                title="Vista compacta"
                data-testid="view-mode-compact"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Activity List */}
          {filteredActivities.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Target className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">No hay actividades</p>
                {isProfessor && (
                  <p className="text-sm text-slate-400">Crea actividades para que tus estudiantes ganen coins y XP</p>
                )}
              </CardContent>
            </Card>
          ) : viewMode === 'compact' ? (
            /* ── Compact / Table View ── */
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="compact-view">
              {/* Table Header */}
              <div className="grid grid-cols-[1fr_auto_auto_auto] sm:grid-cols-[1fr_100px_70px_70px] gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                <span>Actividad</span>
                <span className="text-center hidden sm:block">Estado</span>
                <span className="text-right">Coins</span>
                <span className="text-right">XP</span>
              </div>
              {/* Rows */}
              <div className="divide-y divide-slate-100">
                {filteredActivities.map(activity => {
                  const totalXP = getTotalXP(activity);
                  const isSelected = selectedActivity?.id === activity.id;
                  const myStatus = getMyActivityStatus(activity.id);
                  const subStatus = getSubscriptionStatus(activity.id);
                  const isCompleted = subStatus === 'completed' || myStatus === 'completed';
                  const isInProgress = myStatus === 'in_progress';
                  const isPending = myStatus === 'pending_approval' && !isCompleted;
                  
                  return (
                    <div
                      key={activity.id}
                      onClick={() => handleSelectActivity(activity)}
                      className={`grid grid-cols-[1fr_auto_auto_auto] sm:grid-cols-[1fr_100px_70px_70px] gap-2 items-center px-3 py-2.5 cursor-pointer transition-colors duration-150 ${
                        isSelected 
                          ? 'bg-blue-50 border-l-2 border-l-blue-500' 
                          : 'hover:bg-slate-50 border-l-2 border-l-transparent'
                      }`}
                      data-testid={`activity-item-${activity.id}`}
                    >
                      {/* Title + badges */}
                      <div className="flex items-center gap-2 min-w-0">
                        {activity.category_color && (
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: activity.category_color }} />
                        )}
                        <span className={`text-sm truncate ${isSelected ? 'font-semibold text-blue-900' : 'font-medium text-slate-800'}`}>
                          {activity.title}
                        </span>
                        {activity.is_group_activity && (
                          <UsersRound className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                        )}
                        {activity.is_recurring && (
                          <Repeat className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                        )}
                      </div>
                      {/* Status */}
                      <div className="text-center hidden sm:block">
                        {isCompleted ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                            <CheckCircle2 className="w-3 h-3" /> Hecha
                          </span>
                        ) : isPending ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                            <Clock className="w-3 h-3" /> Pendiente
                          </span>
                        ) : isInProgress ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                            <Play className="w-3 h-3" /> Progreso
                          </span>
                        ) : !isProfessor ? (
                          <span className="text-[11px] text-slate-400">-</span>
                        ) : null}
                      </div>
                      {/* Coins */}
                      <div className="flex items-center justify-end gap-1 text-amber-600">
                        <Coins className="w-3.5 h-3.5" />
                        <span className="text-sm font-semibold">{activity.coin_value}</span>
                      </div>
                      {/* XP */}
                      <div className="flex items-center justify-end gap-1 text-purple-600">
                        {totalXP > 0 ? (
                          <>
                            <Zap className="w-3.5 h-3.5" />
                            <span className="text-sm font-semibold">{totalXP}</span>
                          </>
                        ) : (
                          <span className="text-sm text-slate-300">-</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredActivities.map(activity => {
                const totalXP = getTotalXP(activity);
                const isSelected = selectedActivity?.id === activity.id;
                const myStatus = getMyActivityStatus(activity.id);
                const subStatus = getSubscriptionStatus(activity.id);
                const isCompleted = subStatus === 'completed' || myStatus === 'completed';
                const isInProgress = myStatus === 'in_progress';
                
                return (
                  <div
                    key={activity.id}
                    onClick={() => handleSelectActivity(activity)}
                    className={`p-3 sm:p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50/50 shadow-md ring-1 ring-blue-500/20' 
                        : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm hover:-translate-y-[1px]'
                    }`}
                    data-testid={`activity-item-${activity.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {activity.category_color && (
                            <div 
                              className="w-2 h-2 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: activity.category_color }} 
                            />
                          )}
                          <h4 className="font-medium text-slate-900 truncate">{activity.title}</h4>
                          {!activity.is_active && (
                            <Badge variant="outline" className="text-xs">Inactiva</Badge>
                          )}
                          {activity.is_recurring && (
                            <Badge variant="outline" className="text-xs border-blue-300 text-blue-600">Recurrente</Badge>
                          )}
                          {activity.is_group_activity && (
                            <Badge variant="outline" className="text-xs border-purple-300 text-purple-600">
                              <UsersRound className="w-3 h-3 mr-1" />
                              Grupal
                            </Badge>
                          )}
                          {isCompleted && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Completada
                            </Badge>
                          )}
                          {myStatus === 'pending_approval' && !isCompleted && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              Pendiente
                            </Badge>
                          )}
                          {isInProgress && !isCompleted && myStatus !== 'pending_approval' && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              <Play className="w-3 h-3 mr-1" />
                              En Progreso
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">{stripHtml(activity.description).slice(0, 80)}</p>
                      </div>
                      <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                        <div className="flex items-center gap-1 text-amber-600">
                          <Coins className="w-4 h-4" />
                          <span className="font-medium">{activity.coin_value}</span>
                        </div>
                        {totalXP > 0 && (
                          <div className="flex items-center gap-1 text-purple-600">
                            <Zap className="w-4 h-4" />
                            <span className="font-medium">{totalXP}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Column: Detail Panel / Calendar / Summary - sticky */}
        <div ref={detailRef} className="hidden lg:block lg:overflow-y-auto lg:pb-4 space-y-4 scrollbar-thin">
          {/* Activity Detail Panel */}
          {selectedActivity ? (
            <Card className="border-blue-200 bg-gradient-to-br from-white to-blue-50/30">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {selectedActivity.category_name && (
                      <Badge 
                        variant="outline" 
                        className="mb-2"
                        style={{ borderColor: selectedActivity.category_color, color: selectedActivity.category_color }}
                      >
                        {selectedActivity.category_name}
                      </Badge>
                    )}
                    <CardTitle className="font-heading text-lg">{selectedActivity.title}</CardTitle>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => setSelectedActivity(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-slate-600 overflow-hidden"><RichTextDisplay content={selectedActivity.description} /></div>
                
                {/* Rewards */}
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg">
                    <Coins className="w-5 h-5 text-amber-500" />
                    <span className="font-bold text-amber-700">{selectedActivity.coin_value} Coins</span>
                    {selectedActivity.is_group_activity && (
                      <Badge className="bg-purple-100 text-purple-700 text-[10px] ml-1">Grupo</Badge>
                    )}
                  </div>
                  {getTotalXP(selectedActivity) > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg">
                      <Zap className="w-5 h-5 text-purple-500" />
                      <span className="font-bold text-purple-700">{getTotalXP(selectedActivity)} XP</span>
                    </div>
                  )}
                </div>
                
                {/* XP Breakdown */}
                {selectedActivity.xp_breakdown && selectedActivity.xp_breakdown.length > 0 && (
                  <div className="p-3 bg-purple-50/50 rounded-lg">
                    <p className="text-xs text-purple-600 font-medium mb-2">Desglose de XP:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedActivity.xp_breakdown.map((xp, idx) => (
                        <Badge key={idx} variant="outline" className="bg-white">
                          {xp.skill_name}: {xp.xp} XP
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Due date */}
                {selectedActivity.due_date && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <CalendarIcon className="w-4 h-4" />
                    <span>Fecha límite: {formatDate(selectedActivity.due_date)}</span>
                  </div>
                )}
                
                {/* Group/Recurring indicators */}
                {selectedActivity.is_recurring && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Actividad recurrente - se puede otorgar múltiples veces</span>
                  </div>
                )}
                
                {selectedActivity.is_group_activity && (
                  <div className="flex items-center gap-2 text-sm text-purple-600 p-2 bg-purple-50 rounded-lg">
                    <UsersRound className="w-4 h-4" />
                    <span>Actividad Grupal - los coins se depositan en el wallet del grupo. El XP es individual.</span>
                  </div>
                )}
                
                {/* Actions */}
                <div className="pt-3 border-t border-slate-200 space-y-2">
                  {isProfessor ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleOpenEdit(selectedActivity)}>
                          <Pencil className="w-3 h-3 mr-1" />
                          Editar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleViewStatuses(selectedActivity.id)}>
                          <Eye className="w-3 h-3 mr-1" />
                          Estados
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(selectedActivity.id)}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Eliminar
                        </Button>
                      </div>
                      
                      {/* Batch Award Button */}
                      {selectedActivity.is_group_activity ? (
                        <div className="space-y-2">
                          <div className="p-2 bg-purple-50 rounded-lg text-center">
                            <p className="text-purple-700 font-medium text-xs flex items-center justify-center gap-1">
                              <UsersRound className="w-3 h-3" />
                              Actividad Grupal - coins van al wallet del grupo
                            </p>
                          </div>
                          <Button 
                            className="w-full bg-purple-500 hover:bg-purple-600"
                            onClick={() => handleOpenBatchAward(selectedActivity)}
                            data-testid="batch-award-btn"
                          >
                            <UsersRound className="w-4 h-4 mr-2" />
                            Otorgar a Grupo
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          className="w-full bg-amber-500 hover:bg-amber-600"
                          onClick={() => handleOpenBatchAward(selectedActivity)}
                          data-testid="batch-award-btn"
                        >
                          <Award className="w-4 h-4 mr-2" />
                          Otorgar Coins a Estudiantes
                        </Button>
                      )}
                      
                      {/* Completion counts for recurring */}
                      {selectedActivity.is_recurring && (
                        <Button 
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => loadCompletionCounts(selectedActivity.id)}
                        >
                          <Repeat className="w-3 h-3 mr-1" />
                          Ver Conteo de Completaciones
                        </Button>
                      )}
                      
                      {/* Show completion counts */}
                      {completionCounts && completionCounts.activity_id === selectedActivity.id && (
                        <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                          <h5 className="text-sm font-medium mb-2">
                            Completaciones Totales: {completionCounts.total_completions}
                          </h5>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {completionCounts.students.map(s => (
                              <div key={s.student_user_id} className="flex justify-between text-xs">
                                <span>{s.student_name}</span>
                                <span className="font-medium text-amber-600">
                                  {s.completion_count}x = {s.total_coins_earned} coins
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto py-1 px-2 text-xs text-slate-500 hover:text-blue-600"
                          onClick={() => loadSubscribers(selectedActivity.id)}
                          data-testid="view-subscribers-btn"
                        >
                          <Users className="w-3 h-3 mr-1" />
                          {selectedActivity.subscriber_count} suscritos — Ver lista
                        </Button>
                      </div>
                      
                      {/* Subscribers list */}
                      {subscribers && subscribers.activity_id === selectedActivity.id && (
                        <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                          <p className="text-xs font-medium text-blue-700 mb-2">Estudiantes Suscritos ({subscribers.list.length})</p>
                          {subscribers.list.length === 0 ? (
                            <p className="text-xs text-slate-500">Ningún estudiante suscrito aún</p>
                          ) : (
                            <div className="space-y-1.5 max-h-40 overflow-y-auto">
                              {subscribers.list.map(sub => (
                                <div key={sub.subscription.student_user_id} className="flex items-center gap-2 text-xs">
                                  <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                    {sub.student_photo_url ? (
                                      <img src={sub.student_photo_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="text-[10px] font-medium text-slate-600">{sub.student_name?.charAt(0)}</span>
                                    )}
                                  </div>
                                  <span className="text-slate-700 flex-1 truncate">{sub.student_name}</span>
                                  {sub.coins_awarded ? (
                                    <Badge className="text-[10px] h-4 px-1 bg-emerald-100 text-emerald-700 border-emerald-200">
                                      Coins otorgados
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-[10px] h-4 px-1">
                                      {sub.subscription.status === 'completed' ? 'Completada' : sub.subscription.status === 'in_progress' ? 'En progreso' : sub.subscription.status === 'pending_approval' ? 'Pendiente' : 'Suscrito'}
                                    </Badge>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    (() => {
                      const myStatus = getMyActivityStatus(selectedActivity.id);
                      const subscribed = isSubscribed(selectedActivity.id);
                      const subStatus = getSubscriptionStatus(selectedActivity.id);
                      
                      if (subStatus === 'completed' || myStatus === 'completed') {
                        return (
                          <div className="p-3 bg-emerald-50 rounded-lg text-center">
                            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                            <p className="text-emerald-700 font-medium">Actividad Completada!</p>
                          </div>
                        );
                      } else if (myStatus === 'pending_approval') {
                        return (
                          <div className="p-3 bg-amber-50 rounded-lg text-center">
                            <Clock className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                            <p className="text-amber-700 font-medium text-sm">Pendiente de Aprobación</p>
                            <p className="text-amber-600 text-xs mt-1">Tu profesor revisará tu entrega</p>
                          </div>
                        );
                      } else if (myStatus === 'in_progress') {
                        const isGroupAct = selectedActivity.is_group_activity;
                        const canComplete = !isGroupAct || (myGroupInfo && myGroupInfo.is_leader);
                        return (
                          <div className="space-y-2">
                            {isGroupAct && (
                              <div className="p-2 bg-purple-50 rounded-lg text-center">
                                <p className="text-purple-700 text-xs font-medium flex items-center justify-center gap-1">
                                  <UsersRound className="w-3 h-3" />
                                  Actividad Grupal - coins van al wallet del grupo
                                </p>
                              </div>
                            )}
                            {canComplete ? (
                              <Button 
                                className="w-full bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleMarkCompleted(selectedActivity.id)}
                                data-testid="mark-completed-btn"
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Marcar como Completada
                              </Button>
                            ) : (
                              <div className="p-2 bg-slate-50 rounded-lg text-center">
                                <p className="text-slate-600 text-xs">Solo el líder del grupo puede enviar esta actividad para aprobación</p>
                              </div>
                            )}
                            <div className="p-2 bg-blue-50 rounded-lg text-center">
                              <p className="text-blue-700 font-medium text-xs">En Progreso</p>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="w-full"
                              onClick={() => handleUnsubscribe(selectedActivity.id)}
                            >
                              Cancelar Suscripción
                            </Button>
                          </div>
                        );
                      } else if (subscribed) {
                        return (
                          <div className="space-y-2">
                            <Button 
                              className="w-full"
                              onClick={() => handleStartActivity(selectedActivity.id)}
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Iniciar Actividad
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="w-full"
                              onClick={() => handleUnsubscribe(selectedActivity.id)}
                            >
                              Cancelar Suscripción
                            </Button>
                          </div>
                        );
                      } else {
                        return (
                          <Button 
                            className="w-full"
                            onClick={() => handleSubscribe(selectedActivity.id)}
                          >
                            Suscribirme a esta Actividad
                          </Button>
                        );
                      }
                    })()
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 bg-slate-50/50">
              <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                <Target className="w-10 h-10 text-slate-300 mb-3" />
                <p className="text-slate-500 text-sm">Selecciona una actividad para ver sus detalles</p>
              </CardContent>
            </Card>
          )}

          {/* Calendar */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Calendario</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() - 1))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium min-w-[120px] text-center">
                    {format(currentMonth, 'MMMM yyyy', { locale: es })}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() + 1))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['D', 'L', 'M', 'X', 'J', 'V', 'S'].map(d => (
                  <div key={d} className="text-center text-xs text-slate-400 font-medium py-1">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: getFirstDayOffset() }).map((_, idx) => (
                  <div key={`empty-${idx}`} className="aspect-square" />
                ))}
                {getDaysInMonth().map((day, idx) => {
                  const dayActivities = getActivitiesForDay(day);
                  const isToday = isSameDay(day, new Date());
                  
                  return (
                    <div
                      key={idx}
                      className={`aspect-square flex flex-col items-center justify-center rounded-lg text-sm relative ${
                        isToday ? 'bg-blue-100 text-blue-700 font-bold' : 'hover:bg-slate-100'
                      }`}
                    >
                      {day.getDate()}
                      {dayActivities.length > 0 && (
                        <div className="absolute bottom-1 flex gap-0.5">
                          {dayActivities.slice(0, 3).map((a, i) => (
                            <div 
                              key={i} 
                              className="w-1.5 h-1.5 rounded-full" 
                              style={{ backgroundColor: a.category_color || '#3B82F6' }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          {isProfessor && completionsSummary && (
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">Resumen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-heading font-bold text-emerald-600 mb-3">
                  {completionsSummary.total_completions} completadas
                </p>
                <div className="space-y-2">
                  {completionsSummary.activities_summary?.slice(0, 5).map(item => (
                    <div 
                      key={item.activity.id} 
                      className="flex items-center justify-between text-sm cursor-pointer hover:bg-slate-50 p-1 rounded"
                      onClick={() => {
                        const act = activities.find(a => a.id === item.activity.id);
                        if (act) setSelectedActivity(act);
                      }}
                    >
                      <span className="text-slate-600 truncate flex-1">{item.activity.title}</span>
                      <Badge variant="secondary">{item.completion_count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {!isProfessor && myActivitiesStatus && (
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">Mi Progreso</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div 
                    className={`flex items-center justify-between cursor-pointer p-1.5 rounded transition-colors ${filterStatus === 'all' ? 'bg-slate-100 ring-1 ring-slate-300' : 'hover:bg-slate-50'}`}
                    onClick={() => setFilterStatus(filterStatus === 'all' ? 'all' : 'all')}
                    data-testid="filter-all"
                  >
                    <span className="text-slate-600 text-sm font-medium">Todas</span>
                    <Badge variant="secondary">{activities.length}</Badge>
                  </div>
                  <div 
                    className={`flex items-center justify-between cursor-pointer p-1.5 rounded transition-colors ${filterStatus === 'not_started' ? 'bg-slate-100 ring-1 ring-slate-300' : 'hover:bg-slate-50'}`}
                    onClick={() => setFilterStatus(filterStatus === 'not_started' ? 'all' : 'not_started')}
                    data-testid="filter-not-started"
                  >
                    <span className="text-slate-500 text-sm">No iniciadas</span>
                    <Badge variant="secondary">{myActivitiesStatus.summary?.not_started || 0}</Badge>
                  </div>
                  <div 
                    className={`flex items-center justify-between cursor-pointer p-1.5 rounded transition-colors ${filterStatus === 'in_progress' ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-blue-50'}`}
                    onClick={() => setFilterStatus(filterStatus === 'in_progress' ? 'all' : 'in_progress')}
                    data-testid="filter-in-progress"
                  >
                    <span className="text-blue-600 text-sm">En progreso</span>
                    <Badge className="bg-blue-100 text-blue-700">{myActivitiesStatus.summary?.in_progress || 0}</Badge>
                  </div>
                  <div 
                    className={`flex items-center justify-between cursor-pointer p-1.5 rounded transition-colors ${filterStatus === 'completed' ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'hover:bg-emerald-50'}`}
                    onClick={() => setFilterStatus(filterStatus === 'completed' ? 'all' : 'completed')}
                    data-testid="filter-completed"
                  >
                    <span className="text-emerald-600 text-sm">Completadas</span>
                    <Badge className="bg-emerald-100 text-emerald-700">{myActivitiesStatus.summary?.completed || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Mobile Activity Detail Drawer */}
      {mobileDetailOpen && selectedActivity && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-activity-drawer">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileDetailOpen(false)}
          />
          {/* Drawer */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto animate-in slide-in-from-bottom duration-300 safe-bottom">
            {/* Handle */}
            <div className="sticky top-0 bg-white rounded-t-2xl z-10 pt-3 pb-2 px-4 border-b border-slate-100">
              <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-3" />
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-base text-slate-900 truncate pr-2">{selectedActivity.title}</h3>
                <Button 
                  size="sm" variant="ghost" 
                  onClick={() => setMobileDetailOpen(false)}
                  className="h-8 w-8 p-0 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {/* Content */}
            <div className="p-4 space-y-4">
              {selectedActivity.category_name && (
                <Badge 
                  variant="outline" 
                  style={{ borderColor: selectedActivity.category_color, color: selectedActivity.category_color }}
                >
                  {selectedActivity.category_name}
                </Badge>
              )}
              <div className="text-sm text-slate-600"><RichTextDisplay content={selectedActivity.description} /></div>
              
              {/* Rewards */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg">
                  <Coins className="w-5 h-5 text-amber-500" />
                  <span className="font-bold text-amber-700">{selectedActivity.coin_value} Coins</span>
                </div>
                {getTotalXP(selectedActivity) > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg">
                    <Zap className="w-5 h-5 text-purple-500" />
                    <span className="font-bold text-purple-700">{getTotalXP(selectedActivity)} XP</span>
                  </div>
                )}
              </div>

              {selectedActivity.due_date && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CalendarIcon className="w-4 h-4" />
                  <span>Fecha limite: {formatDate(selectedActivity.due_date)}</span>
                </div>
              )}

              {selectedActivity.is_group_activity && (
                <div className="flex items-center gap-2 text-sm text-purple-600 p-2 bg-purple-50 rounded-lg">
                  <UsersRound className="w-4 h-4" />
                  <span>Actividad Grupal - coins van al wallet del grupo</span>
                </div>
              )}

              {/* Actions */}
              <div className="pt-3 border-t border-slate-200 space-y-2">
                {isProfessor ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setMobileDetailOpen(false); handleOpenEdit(selectedActivity); }}>
                        <Pencil className="w-3 h-3 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleViewStatuses(selectedActivity.id)}>
                        <Eye className="w-3 h-3 mr-1" /> Estados
                      </Button>
                    </div>
                    <Button 
                      className={`w-full ${selectedActivity.is_group_activity ? 'bg-purple-500 hover:bg-purple-600' : 'bg-amber-500 hover:bg-amber-600'}`}
                      onClick={() => { setMobileDetailOpen(false); handleOpenBatchAward(selectedActivity); }}
                    >
                      {selectedActivity.is_group_activity ? (
                        <><UsersRound className="w-4 h-4 mr-2" /> Otorgar a Grupo</>
                      ) : (
                        <><Award className="w-4 h-4 mr-2" /> Otorgar Coins</>
                      )}
                    </Button>
                  </div>
                ) : (
                  (() => {
                    const myStatus = getMyActivityStatus(selectedActivity.id);
                    const subscribed = isSubscribed(selectedActivity.id);
                    const subStatus = getSubscriptionStatus(selectedActivity.id);
                    if (subStatus === 'completed' || myStatus === 'completed') {
                      return (
                        <div className="p-3 bg-emerald-50 rounded-lg text-center">
                          <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-1" />
                          <p className="text-emerald-700 font-medium">Actividad Completada!</p>
                        </div>
                      );
                    } else if (myStatus === 'pending_approval') {
                      return (
                        <div className="p-3 bg-amber-50 rounded-lg text-center">
                          <Clock className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                          <p className="text-amber-700 font-medium text-sm">Pendiente de Aprobacion</p>
                        </div>
                      );
                    } else if (myStatus === 'in_progress') {
                      return (
                        <div className="space-y-2">
                          <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => { setMobileDetailOpen(false); handleMarkCompleted(selectedActivity.id); }}>
                            <Send className="w-4 h-4 mr-2" /> Marcar como Completada
                          </Button>
                        </div>
                      );
                    } else if (subscribed) {
                      return (
                        <Button className="w-full" onClick={() => { handleStartActivity(selectedActivity.id); setMobileDetailOpen(false); }}>
                          <Play className="w-4 h-4 mr-2" /> Iniciar Actividad
                        </Button>
                      );
                    } else {
                      return (
                        <Button className="w-full" onClick={() => { handleSubscribe(selectedActivity.id); setMobileDetailOpen(false); }}>
                          Suscribirme a esta Actividad
                        </Button>
                      );
                    }
                  })()
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingActivity ? 'Editar Actividad' : 'Nueva Actividad'}
              </DialogTitle>
              <DialogDescription>
                {editingActivity ? 'Modifica los datos de la actividad' : 'Crea una actividad para que los estudiantes ganen coins y XP'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input
                  placeholder="Ej: Tarea de Investigación"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  data-testid="activity-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(val) => setFormData({...formData, description: val})}
                  placeholder="Describe la actividad..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Coins</Label>
                  <div className="relative">
                    <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                    <Input
                      type="number"
                      min="1"
                      value={formData.coin_value}
                      onChange={(e) => setFormData({...formData, coin_value: parseInt(e.target.value) || 0})}
                      className="pl-9"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>XP Avanzado</Label>
                    <Switch
                      checked={formData.use_multi_xp}
                      onCheckedChange={(checked) => {
                        setFormData({
                          ...formData, 
                          use_multi_xp: checked,
                          xp_breakdown: checked && objectives.length > 0 
                            ? objectives.map(o => ({ skill_id: o.id, xp: 0 }))
                            : []
                        });
                      }}
                    />
                  </div>
                  {!formData.use_multi_xp && (
                    <div className="relative">
                      <Zap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-500" />
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.xp_value}
                        onChange={(e) => setFormData({...formData, xp_value: parseInt(e.target.value) || 0})}
                        className="pl-9"
                        placeholder="XP (0-100)"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {formData.use_multi_xp && objectives.length > 0 && (
                <div className="space-y-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-purple-700 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    XP por Habilidad
                  </Label>
                  <div className="space-y-2">
                    {objectives.map(obj => {
                      const currentXp = formData.xp_breakdown.find(x => x.skill_id === obj.id)?.xp || 0;
                      return (
                        <div key={obj.id} className="flex items-center justify-between gap-2">
                          <span className="text-sm text-slate-700 flex-1">{obj.name}</span>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            className="w-20 h-8"
                            value={currentXp}
                            onChange={(e) => {
                              const newVal = parseInt(e.target.value) || 0;
                              setFormData({
                                ...formData,
                                xp_breakdown: formData.xp_breakdown.map(x => 
                                  x.skill_id === obj.id ? { ...x, xp: newVal } : x
                                )
                              });
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-purple-600 mt-2">
                    Total: {formData.xp_breakdown.reduce((sum, x) => sum + (x.xp || 0), 0)} XP
                  </p>
                </div>
              )}
              
              {formData.use_multi_xp && objectives.length === 0 && (
                <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                  Primero crea habilidades en la pestaña Gamificación.
                </p>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select value={formData.category_id || "_none"} onValueChange={(v) => setFormData({...formData, category_id: v === "_none" ? "" : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Sin categoría</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                            {cat.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!formData.use_multi_xp && (
                  <div className="space-y-2">
                    <Label>Habilidad (XP)</Label>
                    <Select value={formData.learning_objective_id || "_none"} onValueChange={(v) => setFormData({...formData, learning_objective_id: v === "_none" ? "" : v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">General</SelectItem>
                        {objectives.map(obj => (
                          <SelectItem key={obj.id} value={obj.id}>{obj.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Fecha Límite (opcional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.due_date ? formatDate(formData.due_date) : 'Seleccionar fecha'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.due_date ? new Date(formData.due_date) : undefined}
                      onSelect={(date) => setFormData({
                        ...formData,
                        due_date: date ? format(date, 'yyyy-MM-dd') : ''
                      })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Activa</Label>
                  <p className="text-xs text-slate-500">Solo las actividades activas son visibles para estudiantes</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Recurrente</Label>
                  <p className="text-xs text-slate-500">Permite otorgar coins múltiples veces al mismo estudiante</p>
                </div>
                <Switch
                  checked={formData.is_recurring}
                  onCheckedChange={(checked) => setFormData({...formData, is_recurring: checked})}
                  data-testid="activity-recurring-toggle"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Actividad Grupal</Label>
                  <p className="text-xs text-slate-500">Los coins van al wallet del grupo. El XP se otorga individualmente a cada miembro.</p>
                </div>
                <Switch
                  checked={formData.is_group_activity}
                  onCheckedChange={(checked) => setFormData({...formData, is_group_activity: checked})}
                  data-testid="activity-group-toggle"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} data-testid="submit-activity">
                {submitting ? 'Guardando...' : (editingActivity ? 'Guardar Cambios' : 'Crear Actividad')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Activity Status Dialog (Professor) */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Estados de Actividad: {selectedActivityStatuses?.activity_title}
            </DialogTitle>
            <DialogDescription>
              Ver y modificar el estado de cada estudiante
            </DialogDescription>
          </DialogHeader>
          
          {selectedActivityStatuses && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-600">{selectedActivityStatuses.summary.not_started}</p>
                  <p className="text-xs text-slate-500">No iniciada</p>
                </div>
                <div className="text-center p-2 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{selectedActivityStatuses.summary.in_progress}</p>
                  <p className="text-xs text-blue-500">En progreso</p>
                </div>
                <div className="text-center p-2 bg-emerald-50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-600">{selectedActivityStatuses.summary.completed}</p>
                  <p className="text-xs text-emerald-500">Completada</p>
                </div>
              </div>
              
              <div className="space-y-2">
                {selectedActivityStatuses.students.map(student => (
                  <div 
                    key={student.student_user_id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                        {student.student_photo_url ? (
                          <img src={student.student_photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs font-medium text-slate-600">
                            {student.student_name?.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{student.student_name}</p>
                        <Badge 
                          variant="outline" 
                          className={
                            student.status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                            student.status === 'in_progress' ? 'bg-blue-50 text-blue-700' :
                            'bg-slate-50 text-slate-500'
                          }
                        >
                          {student.status === 'completed' ? 'Completada' : 
                           student.status === 'in_progress' ? 'En progreso' : 'No iniciada'}
                        </Badge>
                      </div>
                    </div>
                    <Select 
                      value={student.status} 
                      onValueChange={(v) => handleChangeStudentStatus(
                        selectedActivityStatuses.activity_id, 
                        student.student_user_id, 
                        v
                      )}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">No iniciada</SelectItem>
                        <SelectItem value="in_progress">En progreso</SelectItem>
                        <SelectItem value="completed">Completada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Batch Award Dialog */}
      <Dialog open={batchAwardDialogOpen} onOpenChange={setBatchAwardDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Otorgar Coins: {batchAwardActivity?.title}
            </DialogTitle>
            <DialogDescription>
              {batchAwardActivity?.is_recurring ? (
                <span className="text-blue-600">Esta actividad es recurrente, se puede otorgar múltiples veces.</span>
              ) : (
                <span className="text-slate-500">Los estudiantes que ya completaron esta actividad serán omitidos.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Activity info */}
            <div className="flex items-center gap-4 p-3 bg-amber-50 rounded-lg flex-wrap">
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-amber-500" />
                <span className="font-bold text-amber-700">{batchAwardActivity?.coin_value} Coins</span>
              </div>
              {batchAwardActivity?.xp_value > 0 && (
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-500" />
                  <span className="font-bold text-purple-700">{batchAwardActivity?.xp_value} XP</span>
                </div>
              )}
              {batchAwardActivity?.is_group_activity && (
                <Badge className="bg-purple-100 text-purple-700 text-xs">
                  <UsersRound className="w-3 h-3 mr-1" />
                  Actividad Grupal
                </Badge>
              )}
            </div>
            
            {batchAwardActivity?.is_group_activity && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-700">
                  Los <strong>coins</strong> se depositarán en el <strong>wallet del grupo</strong>.
                  El <strong>XP</strong> se otorgará individualmente a cada miembro.
                </p>
              </div>
            )}
            
            {/* Note field */}
            <div>
              <Label>Nota (opcional)</Label>
              <Input 
                value={batchAwardNote}
                onChange={(e) => setBatchAwardNote(e.target.value)}
                placeholder="Ej: Participación semana 3"
              />
            </div>
            
            {/* Award Mode Tabs */}
            <Tabs value={awardMode} onValueChange={setAwardMode}>
              <TabsList className="w-full">
                <TabsTrigger 
                  value="individual" 
                  className="flex-1"
                  disabled={batchAwardActivity?.is_group_activity}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Estudiantes Individuales
                </TabsTrigger>
                <TabsTrigger value="group" className="flex-1" disabled={groups.length === 0}>
                  <UsersRound className="w-4 h-4 mr-2" />
                  Por Grupo ({groups.length})
                </TabsTrigger>
              </TabsList>
              
              {/* Individual Mode */}
              <TabsContent value="individual" className="mt-4">
                {/* Select all */}
                <div className="flex items-center justify-between border-b pb-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={selectedStudents.length === students.filter(s => !awardedStudentIds.has(s.user_id)).length && students.filter(s => !awardedStudentIds.has(s.user_id)).length > 0}
                      onCheckedChange={selectAllStudents}
                    />
                    <Label className="cursor-pointer" onClick={selectAllStudents}>
                      Seleccionar disponibles ({students.filter(s => !awardedStudentIds.has(s.user_id)).length} de {students.length})
                    </Label>
                  </div>
                  <Badge variant="outline">
                    {selectedStudents.length} seleccionados
                  </Badge>
                </div>
                
                {awardedStudentIds.size > 0 && (
                  <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs text-green-700 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {awardedStudentIds.size} estudiante(s) ya recibieron esta recompensa
                    </p>
                  </div>
                )}
                
                {/* Student list */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {students.map(student => {
                    const isAwarded = awardedStudentIds.has(student.user_id);
                    return (
                    <div 
                      key={student.user_id}
                      className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                        isAwarded
                          ? 'bg-green-50 border border-green-200 opacity-70 cursor-not-allowed'
                          : selectedStudents.includes(student.user_id) 
                            ? 'bg-amber-50 border border-amber-200 cursor-pointer' 
                            : 'bg-slate-50 hover:bg-slate-100 cursor-pointer'
                      }`}
                      onClick={() => !isAwarded && toggleStudentSelection(student.user_id)}
                      data-testid={`award-student-${student.user_id}`}
                    >
                      <Checkbox 
                        checked={isAwarded || selectedStudents.includes(student.user_id)}
                        onCheckedChange={() => !isAwarded && toggleStudentSelection(student.user_id)}
                        disabled={isAwarded}
                      />
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {student.photo_url ? (
                          <img src={student.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs font-medium text-slate-600">
                            {student.user_name?.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{student.user_name}</p>
                        <p className="text-xs text-slate-500">{student.user_email}</p>
                      </div>
                      {isAwarded && (
                        <Badge className="bg-green-100 text-green-700 border-0 text-[10px]" data-testid={`awarded-badge-${student.user_id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Ya otorgado
                        </Badge>
                      )}
                    </div>
                    );
                  })}
                  
                  {students.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No hay estudiantes inscritos</p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* Group Mode */}
              <TabsContent value="group" className="mt-4">
                {groups.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <UsersRound className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No hay grupos creados</p>
                    <p className="text-sm">Ve al tab de Grupos para crear uno</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Selecciona un grupo</Label>
                    
                    {!batchAwardActivity?.is_recurring && awardedGroupIds.size > 0 && (
                      <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-700 flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {awardedGroupIds.size} grupo(s) ya recibieron esta recompensa
                        </p>
                      </div>
                    )}
                    
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {groups.map(group => {
                        const isGroupAwarded = !batchAwardActivity?.is_recurring && awardedGroupIds.has(group.id);
                        return (
                        <div 
                          key={group.id}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                            isGroupAwarded
                              ? 'bg-green-50 border-2 border-green-200 opacity-70 cursor-not-allowed'
                              : selectedGroup === group.id 
                                ? 'bg-amber-50 border-2 border-amber-300 cursor-pointer' 
                                : 'bg-slate-50 hover:bg-slate-100 border border-slate-200 cursor-pointer'
                          }`}
                          onClick={() => !isGroupAwarded && setSelectedGroup(group.id)}
                          data-testid={`award-group-${group.id}`}
                        >
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${group.color}20` }}
                          >
                            <UsersRound className="w-5 h-5" style={{ color: group.color }} />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{group.name}</p>
                            <p className="text-sm text-slate-500">{group.member_count} miembros</p>
                          </div>
                          {isGroupAwarded ? (
                            <Badge className="bg-green-100 text-green-700 border-0 text-[10px]" data-testid={`awarded-group-badge-${group.id}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Ya otorgado
                            </Badge>
                          ) : selectedGroup === group.id ? (
                            <CheckCircle2 className="w-5 h-5 text-amber-500" />
                          ) : null}
                        </div>
                        );
                      })}
                    </div>
                    
                    {selectedGroup && (
                      <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                        <p className="text-sm text-amber-700">
                          {batchAwardActivity?.is_group_activity ? (
                            <>
                              Se depositarán <span className="font-bold">{batchAwardActivity?.coin_value} coins</span> en el 
                              wallet del grupo <span className="font-bold">{groups.find(g => g.id === selectedGroup)?.name}</span>.
                              El XP se otorgará a cada uno de los{' '}
                              <span className="font-bold">{groups.find(g => g.id === selectedGroup)?.member_count || 0}</span> miembros.
                            </>
                          ) : (
                            <>
                              Se otorgarán <span className="font-bold">{batchAwardActivity?.coin_value} coins</span> y XP a cada uno de los{' '}
                              <span className="font-bold">{groups.find(g => g.id === selectedGroup)?.member_count || 0}</span> miembros del grupo.
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchAwardDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleBatchAward}
              disabled={submitting || (awardMode === 'individual' ? selectedStudents.length === 0 : !selectedGroup)}
              className={batchAwardActivity?.is_group_activity ? "bg-purple-500 hover:bg-purple-600" : "bg-amber-500 hover:bg-amber-600"}
            >
              {submitting ? 'Otorgando...' : (
                awardMode === 'individual' 
                  ? `Otorgar a ${selectedStudents.length} estudiantes`
                  : batchAwardActivity?.is_group_activity
                    ? `Otorgar al grupo (coins al wallet grupal)`
                    : `Otorgar al grupo (${groups.find(g => g.id === selectedGroup)?.member_count || 0} miembros)`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
